<template>
  <el-dialog
    :visible="visible"
    :show-close="false"
    width="60%"
    class="edit-detail-dialog"
    center
    @close="handleDialogClose">
    <span slot="title" class="title-container">
      <span class="title-text">{{isEdit ? $t('common.edit') : $t('common.add')}}</span>
      <img
        src="@/assets/close.png"
        class="close-image"
        @click="close"
      />
    </span>
    <el-form class="el-form"  :model="form" ref="refForm" :rules="rules" label-width="180px">
      <el-form-item
        :label="$t('common.productName')"
        prop="productId">
        <el-select
          v-model="form.productId"
          clearable
          filterable
          style="width: 100%;"
          size="small">
          <el-option
            v-for="item in productsList"
            :key="item.id"
            :label="item.productName + ' / ' + item.chipName"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        :label="$t('common.isMixed')"
        prop="isMixed">
        <el-radio-group v-model="form.isMixed">
          <el-radio :label="true">
            {{$t('common.yes')}}
          </el-radio>
          <el-radio :label="false">
            {{$t('common.no')}}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        :label="$t('common.processType')"
        prop="processType">
        <el-select
          v-model="form.processType"
          style="width: 100%;"
          filterable
          size="small">
          <el-option
            v-for="(item, key) in processTypes"
            :key="key"
            :label="item.explain"
            :value="item.val">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        :label="$t('common.lotNo')"
        prop="lotNo">
        <el-input
          v-model.trim="form.lotNo"
          name="lotNo"
          type="text"
          autocomplete="on"
          clearable
          maxlength="32"
          size="small"
        />
      </el-form-item>
      <el-form-item
        :label="$t('common.count')"
        prop="count">
        <el-input
          v-model.trim.number="form.count"
          name="count"
          type="tel"
          autocomplete="on"
          clearable
          maxlength="10"
          size="small"
        />
      </el-form-item>
      <el-form-item
        :label="$t('common.partNo')"
        prop="partNo">
        <el-input
          v-model.trim="form.partNo"
          name="partNo"
          type="text"
          autocomplete="on"
          clearable
          size="small"
          maxlength="64">
        </el-input>
      </el-form-item>
      <el-form-item
        :label="$t('common.endDevicen')"
        prop="endDevicen">
        <el-input
          v-model.trim="form.endDevicen"
          name="endDevicen"
          type="text"
          autocomplete="on"
          clearable
          size="small"
          maxlength="64">
        </el-input>
      </el-form-item>
    </el-form>
    <span slot="footer">
        <el-button size="small" @click="close">{{$t('common.cancel')}}</el-button>
        <el-button
          :loading="loading"
          size="small"
          type="primary"
          @click="save">
            {{isEdit ? $t('common.update') : $t('common.add')}}
        </el-button>
      </span>
  </el-dialog>
</template>

<script>
import {
  addOrderProductDetail,
  updateOrderProductDetail,
} from '@/api/incoOrder';
import {
  validateLotNo,
  validateCount,
} from '@/utils/validate';
import { isPositiveIntegerNum } from '@/utils';

export default {
  name: 'EditDetailDialog',
  props: {
    incoOrderNo: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    editRow: {
      type: Object,
      default: () => {},
    },
    editIndex: {
      type: Number,
    },
    productsList: {
      type: Array,
      default: () => [],
    },
    processTypes: {
      type: Object,
      default: () => {},
    }
  },
  data() {
    return {
      form: {},
      rules: {
        productId: [{ required: true, trigger: 'blur', message: this.$t('validate.productNameCanNotBeEmpty') }],
        isMixed: [{ required: true, trigger: 'blur', message: this.$t('validate.isMixedCanNotBeEmpty') }],
        lotNo: [{ required: true, trigger: 'blur', validator: validateLotNo }],
        count: [{ required: true, trigger: 'blur', validator: validateCount }],
        endDevicen: [{ required: true, trigger: 'blur', message: this.$t('validate.endDevicenCanNotBeEmpty') }],
        processType: [{ required: true, trigger: 'blur', message: this.$t('validate.processTypeCanNotBeEmpty') }],
        partNo: [{ required: true, trigger: 'blur', message: this.$t('validate.partNoCanNotBeEmpty') }]
      },
      loading: false,
    };
  },
  watch: {
    editRow(newVal) {
      this.form = {...newVal};
    },
  },
  methods: {
    handleDialogClose() {
      this.close();
    },
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
      if (this.$refs.refForm && typeof this.$refs.refForm.clearValidate === 'function') {
        this.$refs.refForm.clearValidate();
      }
    },
    save() {
      this.$refs.refForm.validate(async (valid) => {
        if (valid) {
          if (this.isEdit) {
            this.updateOrderProductDetail();
          } else {
            this.addOrderProductDetail();
          }
        }
      });
    },
    async addOrderProductDetail() {
      if (this.visible === false) {
        return;
      }
      const {
        subNo,
        productId,
        lotNo,
        count,
        isMixed,
        processType,
        partNo,
        endDevicen,
      } = this.form;
      const incoOrderNo = this.incoOrderNo;
      this.loading = true;
      try {
        const res = await addOrderProductDetail({
          incoOrderNo,
          productDetail: {
            subNo,
            productId,
            lotNo,
            count,
            isMixed,
            processType,
            partNo,
            endDevicen,
          },
        });
        this.$message({
          type: 'success',
          message: this.$t('common.addSuccess'),
        });
        const {
          data
        } = res;
        this.$emit('add', data.incoOrdersDetailData);
        this.close();
      } catch (e) {
        // do nothing.
      } finally {
        this.loading = false;
      }
    },
    async updateOrderProductDetail() {
      if (this.visible === false) {
        return;
      }
      const {
        subNo,
        productId,
        lotNo,
        count,
        isMixed,
        processType,
        partNo,
        endDevicen,
      } = this.form;
      const incoOrderNo = this.incoOrderNo;
      const editRow = this.editRow;
      this.loading = true;
      try {
        await updateOrderProductDetail({
          incoOrderNo,
          productId: editRow.productId,
          lotNo: editRow.lotNo,
          processType: editRow.processType,
          productDetail: {
            subNo,
            productId,
            lotNo,
            count,
            isMixed,
            processType,
            partNo,
            endDevicen,
          }
        });
        this.$message({
          type: 'success',
          message: this.$t('common.updateSuccess'),
        });
        this.$emit('update', {
          subNo,
          productId,
          isMixed,
          processType,
          lotNo,
          count,
          partNo,
          endDevicen,
          status: editRow.status,
        }, this.editIndex);
        this.close();
      } catch (e) {
        // do nothing.
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
  .title-container {
    display: flex;
    display: -webkit-flex;
    flex-direction: row;
    justify-content: space-between;
    background: -webkit-linear-gradient(#F9F9F9, #EEEEEE);
    background: -o-linear-gradient(#F9F9F9, #EEEEEE);
    background: -moz-linear-gradient(#F9F9F9, #EEEEEE);
    background: linear-gradient(#F9F9F9, #EEEEEE);
    padding: 10px 15px;
    align-items: center;
  }
  .title-text {
    color: #404040;
    font-size: 16px;
  }
  .el-form {
    margin: 0 30px;
    width: 80%;
  }
  .close-image {
    width: 22px;
    height: 22px;
    cursor: pointer;
    padding: 0 10px;
  }
  .small-title {
    font-size: 14px;
    color: #606266;
    line-height: 40px;
  }
</style>
<style>
  .edit-detail-dialog .el-dialog__header {
    padding: 0;
  }
</style>
